import { hashParams, modalValues } from "routes";
import DashboardComponentExports from "components/dashboard/DashboardComponentExports";
import LinkAccountCommonExports from "components/link_account/LinkAccountCommonExports";

import {
  custodianSelector,
  accountLinkingService,
  store,
  custodianLinkNeedsRefreshSelector,
  custodianLinkNeedsReconnectSelector,
  refreshAllConnectedCustodians,
  isZaboReconnectionCandidate,
  isZaboManualEntryCandidate,
  custodianSheetSelector,
  isSafariOnMacOS,
  liveRefreshConnectedCustodian
} from "@kubera/common";

import ZaboReconnectDialog from "./ZaboReconnectDialog";
import GenericMessageDialog from "components/dialog/GenericMessageDialog";

export const linkAccountMode = {
  LINK: "add",
  EDIT: "edit",
  REFRESH: "refresh",
  SELECT_ACCOUNTS: "select_account",
  RECONNECT: "reconnect",
  MANAGE: "manage",
  REAUTH: "reauth" // same as reconnect, but on reauth we will show account list screen and from account list screen it should go to edit accounts in existing connection flow
};

// Since this exports methods store.getState() usage is fine
const staticMethods = {
  linkAccount: (
    history,
    location,
    custodianId,
    linkMode = linkAccountMode.LINK,
    shouldReplace = false,
    regionCode = null,
    category = DashboardComponentExports.getCurrentCategory(),
    isReconnectedFromConnectivityCenter = false,
    showAccountList = false
  ) => {
    const regionCodeParam = regionCode ? `${hashParams.LINK_REGION}=${regionCode}` : "";
    const linkHash = `${hashParams.MODAL}=${modalValues.LINK_ACCOUNT}&${hashParams.LINK_CATEGORY}=${category}&${
      hashParams.ID
    }=${encodeURIComponent(custodianId)}&${hashParams.LINK_MODE}=${linkMode}&${regionCodeParam}&${
      hashParams.IS_RECONNECTED_FROM_CONNECTIVITY_CENTER
    }=${isReconnectedFromConnectivityCenter}&${hashParams.SHOW_ACCOUNT_LIST}=${showAccountList}`;

    if (isSafariOnMacOS()) {
      // Safari did not like the fact that we were opening a link account page after changing
      // the URL hash params and was blocing the new tab as a malicious pop-up. To work around
      // this we are now firing an event listener and this gets preference over the url params
      // when present to open the tab without the need to change the URL hash params first
      const event = new CustomEvent("link_account", { detail: { hash: linkHash } });
      document.dispatchEvent(event);
    } else {
      if (shouldReplace === true) {
        history.replace({ ...location, hash: linkHash });
      } else {
        history.push({ ...location, hash: linkHash });
      }
    }
  },

  linkAccountWithService: (
    history,
    location,
    custodianId,
    linkMode = linkAccountMode.LINK,
    linkingService = null,
    shouldReplace = false,
    category = DashboardComponentExports.getCurrentCategory()
  ) => {
    const linkingServiceParam = linkingService ? `${hashParams.LINK_SERVICE}=${linkingService}` : "";
    if (shouldReplace === true) {
      history.replace({
        ...location,
        hash: `${hashParams.MODAL}=${modalValues.LINK_ACCOUNT}&${hashParams.LINK_CATEGORY}=${category}&${
          hashParams.ID
        }=${encodeURIComponent(custodianId)}&${hashParams.LINK_MODE}=${linkMode}&${linkingServiceParam}`
      });
    } else {
      history.push({
        ...location,
        hash: `${hashParams.MODAL}=${modalValues.LINK_ACCOUNT}&${hashParams.LINK_CATEGORY}=${category}&${
          hashParams.ID
        }=${encodeURIComponent(custodianId)}&${hashParams.LINK_MODE}=${linkMode}&${linkingServiceParam}`
      });
    }
  },

  selectAccounts: (history, location, custodianId, category = DashboardComponentExports.getCurrentCategory()) => {
    const custodian = custodianSelector(store.getState(), custodianId);

    if (custodian && custodian.linkingScreenClosed === true) {
      staticMethods.linkAccount(
        history,
        location,
        custodianId,
        linkAccountMode.SELECT_ACCOUNTS,
        undefined,
        undefined,
        category
      );
    } else {
      LinkAccountCommonExports.reopen(custodianId);
    }
  },

  refreshAccountValues: (
    history,
    location,
    custodianId,
    onSuccess = null,
    onError = null,
    flags,
    liveUpdate = false
  ) => {
    var isRefreshing = false;
    const custodian = custodianSelector(store.getState(), custodianId);

    if (!custodian === true) {
      return;
    }

    const isUneditableConnection = [
      accountLinkingService.CARS,
      accountLinkingService.DOMAINS,
      accountLinkingService.ZILLOW,
      accountLinkingService.AI_APPRAISER
    ].includes(custodian.linkType);

    if (custodian.statusInfo === "FORCE_RECONNECT") {
      staticMethods.reconnect(history, location, custodianId);
    } else if (isZaboReconnectionCandidate(custodian.linkType, custodian.linkProviderId)) {
      ZaboReconnectDialog.show(history, location, true);
    } else if (isZaboManualEntryCandidate(custodian.linkType, custodian.linkProviderId)) {
      ZaboReconnectDialog.show(history, location, false);
    } else if (custodianLinkNeedsRefreshSelector(store.getState(), custodianId) === true) {
      if (isUneditableConnection) {
        GenericMessageDialog.show(history, location);
        return;
      }
      staticMethods.linkAccount(history, location, custodianId, linkAccountMode.REFRESH);
    } else if (custodianLinkNeedsReconnectSelector(store.getState(), custodianId) === true) {
      if (isUneditableConnection) {
        GenericMessageDialog.show(history, location);
        return;
      }
      staticMethods.linkAccount(history, location, custodianId, linkAccountMode.EDIT);
    } else {
      if (liveUpdate) {
        store.dispatch(liveRefreshConnectedCustodian(custodianId, onSuccess, onError, true));
      } else {
        store.dispatch(refreshAllConnectedCustodians(custodianId, onSuccess, onError, flags, true));
      }
      isRefreshing = true;
    }
    return isRefreshing;
  },

  resolveLinkError: (history, location, custodianId, isReconnectedFromConnectivityCenter = false) => {
    const custodian = custodianSelector(store.getState(), custodianId);

    if (
      [
        accountLinkingService.CARS,
        accountLinkingService.DOMAINS,
        accountLinkingService.ZILLOW,
        accountLinkingService.AI_APPRAISER
      ].includes(custodian.linkType)
    ) {
      GenericMessageDialog.show(history, location);
      return;
    }

    const custodianSheet = custodianSheetSelector(store.getState(), custodianId);
    const category = custodianSheet ? custodianSheet.category : undefined;

    if (isZaboReconnectionCandidate(custodian.linkType, custodian.linkProviderId)) {
      ZaboReconnectDialog.show(history, location, true);
    } else if (isZaboManualEntryCandidate(custodian.linkType, custodian.linkProviderId)) {
      ZaboReconnectDialog.show(history, location, false);
    } else if (custodian.statusInfo === "FORCE_RECONNECT") {
      staticMethods.reconnect(history, location, custodianId, false);
    } else if (custodianLinkNeedsRefreshSelector(store.getState(), custodianId) === true) {
      staticMethods.linkAccount(
        history,
        location,
        custodianId,
        linkAccountMode.REFRESH,
        undefined,
        undefined,
        category,
        isReconnectedFromConnectivityCenter
      );
    } else {
      staticMethods.linkAccount(
        history,
        location,
        custodianId,
        linkAccountMode.EDIT,
        undefined,
        undefined,
        category,
        isReconnectedFromConnectivityCenter
      );
    }
  },

  reconnect: (history, location, custodianId, isReconnectedFromConnectivityCenter = false, showAccountList = false) => {
    const custodianSheet = custodianSheetSelector(store.getState(), custodianId);
    const category = custodianSheet ? custodianSheet.category : undefined;

    staticMethods.linkAccount(
      history,
      location,
      custodianId,
      linkAccountMode.RECONNECT,
      false,
      null,
      category,
      isReconnectedFromConnectivityCenter,
      showAccountList
    );
  }
};

export default staticMethods;
